/**
 * Handover — one buyer's gift of 1..N tickets to one named person, at
 * `workspaces/{orgId}/events/{eventId}/handovers/{handoverId}`. The unit is
 * the slice, never the ticket: a couple's two tickets are one handover to
 * one person, which keying by ticket id would make unrepresentable.
 *
 * A sibling collection under the event rather than a field on Ticket, for
 * the containment reason `wallet-token.entity.ts` states in full: the
 * `tickets.checkin.scan` role reads and lists whole Ticket documents and
 * Firestore has no field-level ACL, so a recipient's name carried on Ticket
 * itself would reach every door phone's live stream and offline cache.
 * `TicketSchema` gains no field for this.
 *
 * This document carries no slice token — that lives on `HandoverToken`,
 * default-denied and keyed by the same id. Possession of an access token is
 * write authority over the handover graph (`handTicketsOver`,
 * `takeTicketsBack`), and this document is door-readable, so a token here
 * would turn the door's read grant into the power to re-route or reclaim
 * any handed-over ticket. What the grant does disclose is every recipient
 * name in the event, to every scanning account.
 *
 * `read` and `list` are the only actions, both on `tickets.checkin.scan` +
 * mfa. The door resolves a scanned ticket by `where('ticketIds',
 * 'array-contains', ticketId)` against this event's own subcollection, so
 * `list` is the grant that query needs; both predicates stay auth-only,
 * since a predicate touching `resource.data` downgrades to get-only and
 * would deny that query. No write action of any kind: this collection is
 * written only through the Admin SDK, by the callables that hand tickets on,
 * reclaim them, and report who holds each one.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import type { Actor } from "./workspace.entity.ts";

// The channel the buyer chose to send the link through — closed, because
// each value is a distinct send path: `email` is the deployment's own mail,
// composed and addressed server-side, while `whatsapp` and `share` open a
// client carrier (`wa.me`, `navigator.share`) on a body the page composes.
// Null is the fourth real state, not a missing value: a handover the buyer
// recorded after handing the phone over in person.
export const HandoverChannelSchema = z.enum(["email", "whatsapp", "share"]);
export type HandoverChannel = z.infer<typeof HandoverChannelSchema>;

// Fields justified:
//   name             — the recipient as the buyer typed it; the tag their own
//     card wears and the name the door's verdict shows. The name IS the tag,
//     so the buyer never learns a second concept
//   ticketIds        — the slice itself, the tickets this handover carries
//   parentHandoverId — the chain edge, so a take-back walks down from the root
//   orderId          — the root key: one query returns every handover in this
//     order's tree, which is how a take-back from the buyer's own token
//     reaches a ticket however far it has travelled
//   createdAt        — when the buyer tagged it, which orders the chain's hops
//   sentVia          — which carrier the buyer chose
// Excluded:
//   accessToken — the slice token, on the default-denied `HandoverToken`
//     sibling instead; see the file header
//   sentAt — createdAt already carries when the handover happened, and the
//     record is written before anything is sent, so a second timestamp would
//     be a near-duplicate free to disagree with the first
//   handoverId — this doc's own id restated as a field. WalletToken earns
//     that shape because Apple hands its web service a bare serial with no
//     parent path; nothing here does — the door holds orgId/eventId from the
//     scan it is performing, and the callables hold them through the token
//   orgId / eventId — this document's own path carries both
//   recipientEmail / phone — the record holds no contact detail. On the
//     E-Mail channel the deployment does send the link itself, addressed
//     from a value the buyer types onto `HandTicketsOverInput`: real
//     processing, read once to address that send and written to no document,
//     which is why a second send asks the buyer for the address again. The
//     other channels hand the deployment no address at all. A durable copy
//     would sit on a document the `tickets.checkin.scan` role reads and
//     lists, which Firestore has no field-level ACL to withhold, so every
//     scanning account would hold each recipient's contact data beside their
//     name
//   a live/superseded flag — a record that has handed everything on stands
//     with an empty `ticketIds`, and that emptiness IS the answer: the door
//     resolves a ticket by `array-contains`, which such a record never
//     matches, so it claims nothing. A flag would restate what the array
//     already says and let the door read two answers to "who holds this
//     ticket now"
//   deliveredAt / openedAt — no channel reports arrival. The mail provider
//     states only that it accepted a send, which the call's own response
//     carries back to the sender, and no carrier says anything about a
//     mailbox, so a delivery fact here could only ever be a claim
export const HandoverSchema = z.object({
	// Bounded at one mail body line: RFC 5322 §2.1.1 fixes 78 octets as a
	// line's recommended maximum, and this value is the addressee of the
	// greeting line every message opens with — the mail the deployment
	// composes and the WhatsApp and share bodies the page composes — so a
	// value that alone overruns a line is not a salutation. The bound keeps
	// the field a label rather than a free-text store; it cannot make it one,
	// since an address or a phone number fits under any ceiling loose enough to
	// accept a real name. Telling the buyer what belongs here is the field
	// copy's job, not the schema's — a shape filter that caught an `@` and
	// missed the other three would advertise a sanitisation nothing performs.
	name: z
		.string()
		.min(1)
		.max(78)
		.describe(
			"who holds these tickets now, as the buyer names them — the shortest label that lets the buyer pick their own person out of a list at a glance, and that reads as the addressee of the message the link travels in. Never normalized: it is rendered back to the buyer as their card's own caption and to the door as the holder's name",
		),
	ticketIds: z
		.array(z.string().min(1))
		.describe(
			"the tickets this holder still has door-authority over, all from one order — empty once every one has been handed on, because the record is what the holder's link resolves through and it outlives its tickets until a take-back empties the page too",
		),
	parentHandoverId: z
		.string()
		.min(1)
		.nullish()
		.describe(
			"the handover this one was created from — null at the root, where the buyer's own order token made it. A provenance edge rather than a live pointer: supersession deletes a handover the moment it empties, so this id can name a record that is gone, and a handover whose parent has gone that way is reachable from the root alone",
		),
	orderId: z
		.string()
		.min(1)
		.describe(
			"the order every ticket above belongs to. Derivable by reading any one of those tickets, and carried here anyway: it is the query key that returns this order's whole handover tree in one read, which is what lets a take-back from the buyer's own token reach a ticket wherever it has travelled. It discloses nothing new to the scan role, which already lists Ticket.orderId",
		),
	createdAt: z
		.custom<Timestamp>()
		.describe(
			"when the buyer recorded the handover — written before anything is sent, so a dismissed share sheet or a mail the provider refuses still leaves the tickets tagged",
		),
	sentVia: HandoverChannelSchema.nullish().describe(
		"the carrier the buyer chose. Null is a handover recorded without sending — the phone changed hands in person — which is a settled state, not a half-finished one. It names the channel chosen, never that anything arrived",
	),
});
export type Handover = z.infer<typeof HandoverSchema>;

export const Handover = entity<Actor>()({
	schema: HandoverSchema,
	actions: ["read", "list"] as const,
})
	.pipe(({ can, hasPermission, and, pathVar }) => [
		// The door's direct get, on the same permission its ticket read runs on.
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.checkin.scan", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
		// The door's own resolution: `ticketIds array-contains {ticketId}` over
		// this event's handovers. Auth-only, so the emitted grant stays
		// list-safe — a predicate reading `resource.data` has nothing to
		// evaluate on a list and would deny the query outright.
		can("list").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.checkin.scan", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
		// No create/update/delete: `handTicketsOver`, `takeTicketsBack` and
		// `listHandovers` write and read through the Admin SDK, which bypasses
		// these rules, so any generated write verb here would only widen the
		// surface without serving a caller.
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/handovers/{handoverId}",
		}),
	);
